import React, { useCallback } from 'react';
import { Box, Flex, Heading, Radio, RadioGroup, Text } from '@chakra-ui/react';
import { CurrencyInput } from 'ts/common/components';
import { replaceHtmlEscapeCharacters } from 'ts/common/utils';

export type ClientPaymentOption = 'full' | 'retainer' | 'custom';

interface Props {
    currencyCode?: string;
    customAmount?: number;
    customAmountError?: string;
    customOptionText?: string;
    fullOptionText: string;
    onChange: (x: ClientPaymentOption) => void;
    onCustomAmountBlur?: () => void;
    onCustomAmountChange?: (amount: number) => void;
    retainerOptionText?: string;
    showCustomOption?: boolean;
    showRetainerOption?: boolean;
    title: string;
    value: ClientPaymentOption;
}

const ClientPaymentOptions = ({
    currencyCode,
    customAmount,
    customAmountError,
    customOptionText,
    fullOptionText,
    onChange,
    onCustomAmountBlur,
    onCustomAmountChange,
    retainerOptionText,
    showCustomOption,
    showRetainerOption = true,
    title,
    value
}: Props) => {
    const handleChange = useCallback(
        (nextValue: string) => {
            onChange(nextValue as ClientPaymentOption);
        },
        [onChange]
    );

    const handleCustomAmountChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const numericValue = Number(event.target.value);
            onCustomAmountChange?.(numericValue);
        },
        [onCustomAmountChange]
    );

    return (
        <Flex flexDirection="column" gap="16px">
            <Heading as="h3" size="lg" margin={0}>
                {title}
            </Heading>
            <RadioGroup
                display="flex"
                flexDirection="column"
                gap="12px"
                onChange={handleChange}
                value={value}
            >
                <Radio value="full">{replaceHtmlEscapeCharacters(fullOptionText)}</Radio>
                {showRetainerOption && retainerOptionText && (
                    <Radio value="retainer">
                        {replaceHtmlEscapeCharacters(retainerOptionText)}
                    </Radio>
                )}
                {showCustomOption && customOptionText && (
                    <Box>
                        <Radio value="custom">
                            {replaceHtmlEscapeCharacters(customOptionText)}
                        </Radio>
                        {value === 'custom' && currencyCode && (
                            <Box mt="12px" ml="24px" maxW="200px">
                                <CurrencyInput
                                    currencyCode={currencyCode}
                                    value={customAmount ?? 0}
                                    onBlur={onCustomAmountBlur}
                                    onChange={handleCustomAmountChange}
                                />
                                {customAmountError && (
                                    <Text color="red.500" fontSize="sm" mt="4px">
                                        {customAmountError}
                                    </Text>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </RadioGroup>
        </Flex>
    );
};

export default ClientPaymentOptions;
